import { Injectable, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { NodesService } from './nodes.service';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { AccountRequestService } from '../../auth/service/account-request.service';
import { WorkflowsService } from '../../system/workflows/service/workflows.service';

@Injectable({
    providedIn: 'root'
})
export class NodeTransitionService {
    private transitionStorage = new BehaviorSubject<any>({});

    transitionStorage$ = this.transitionStorage.asObservable();

    storage = {
        chosenToState: {} as any,
        assignToUser: {} as any,
        transitions: [] as any[],
        valid: true,
        stateValid: {} as Record<string, boolean>,
        transitionValid: true,
        callbackFunction: async () => {},
        preFunction: async () => {},
        title: '',
        errorMsg: '',
        showErrorMsg: true,
        transitionToUserMessage: '',
        messageToTransitionUser: '',
        transitioning: false,
        reloadRoute: false
    };

    constructor(
        private nodesService: NodesService,
        private workflowsService: WorkflowsService,
        private accountRequestService: AccountRequestService,
        private router: Router,
        //private alerts: AlertsService,
        private location: Location
    ) {}

    // -------------------- TRANSITIONS --------------------

    getTransitions(node: any): void {
        this.workflowsService.getMyTransitions(node.ID).subscribe({
            next: (res: any) => {
                if (res?.msg?.SUCCESS === false) {
                    //this.alerts.addAlert(res.msg.MSG, 'danger');
                } else if (res?.transitions) {
                    console.log('Transitions', res.transitions);
                    this.set(res.transitions);
                }
            },
            error: (err) => console.error(err)
        });
    }

    set(transitions: any[]): void {
        this.storage.transitions = transitions;
        this.transitionStorage.next(this.storage);
    }

    index(): any[] {
        return this.storage.transitions;
    }

    show(index: number): any {
        return this.storage.transitions[index];
    }

    setValid(flag: boolean): void {
        this.storage.valid = flag;
        this.transitionStorage.next(this.storage);
    }

    setStateValid(stateName: string, flag: boolean): void {
        this.storage.stateValid[stateName] = flag;
        this.transitionStorage.next(this.storage);
    }

    setTransitionValid(flag: boolean): void {
        this.storage.transitionValid = flag;
        this.transitionStorage.next(this.storage);
    }

    async transition(node: any): Promise<void> {
        this.storage.transitioning = true;
        this.transitionStorage.next(this.storage);

        if (!this.storage.chosenToState?.ID) {
            this.storage.transitioning = false;
            this.transitionStorage.next(this.storage);
            return;
        }

        const toStateName = this.storage.chosenToState.NAME;
        const doTransition = async () => {
            try {
                const res = await firstValueFrom(
                    this.nodesService.transition(
                        node,
                        this.storage.chosenToState,
                        this.storage.assignToUser,
                        this.storage.assignToUser ? this.storage.messageToTransitionUser : ''
                    )
                );

                this.storage.valid = true;
                this.transitionStorage.next(this.storage);

                if (typeof this.storage.callbackFunction === 'function') {
                    await this.storage.callbackFunction();
                }

                this.navigateAfterTransition(toStateName);
            } catch (err) {
                console.error(err);
            } finally {
                this.storage.transitioning = false;
                this.transitionStorage.next(this.storage);
            }
        };

        // If preFunction is defined, run that first
        if (typeof this.storage.preFunction === 'function') {
            try {
                const res: any = await this.storage.preFunction();
                if (res?.msg?.SUCCESS || this.checkIfResIsArray(res)) {
                    await doTransition();
                } else {
                    //this.alerts.addAlert(res.data.msg.MSG, 'danger');
                }
            } catch (err) {
                console.error(err);
            } finally {
                this.storage.transitioning = false;
                this.transitionStorage.next(this.storage);
            }
        } else {
            await doTransition();
        }
    }

    private navigateAfterTransition(toStateName: string): void {
        if (this.storage.reloadRoute) {
            this.router.navigate([this.router.url]); // reloads route
            return;
        }

        if (['Completion State', 'Approved State', 'Denied State', 'Void State'].includes(toStateName)) {
            this.router.navigate(['/workbench/my-archive']);
        } else {
            this.router.navigate(['/workbench/my-involved']);
        }
    }

    // -------------------- UTILS & SETTERS --------------------

    setPreFunction(fn: any): void {
        if (typeof fn === 'function') {
            this.storage.preFunction = fn;
            this.transitionStorage.next(this.storage);
        }
    }

    setCallbackFunction(fn: any): void {
        if (typeof fn === 'function') {
            this.storage.callbackFunction = fn;
            this.transitionStorage.next(this.storage);
        }
    }

    clearFunctions(): void {
        this.storage.preFunction = undefined as any;
        this.storage.callbackFunction = undefined as any;
        this.transitionStorage.next(this.storage);
    }

    setErrorMsg(msg: string): void {
        this.storage.errorMsg = 'Note: ' + msg;
        this.transitionStorage.next(this.storage);
    }

    setShowErrorMsg(flag: boolean): void {
        this.storage.showErrorMsg = flag;
        this.transitionStorage.next(this.storage);
    }

    setTitle(title: string): void {
        this.storage.title = title;
        this.transitionStorage.next(this.storage);
    }

    setTransitionToUserMessage(message: string): void {
        this.storage.transitionToUserMessage = message;
        this.transitionStorage.next(this.storage);
    }

    setMessageToTransitionUser(message: string): void {
        this.storage.messageToTransitionUser = message;
        this.transitionStorage.next(this.storage);
    }

    setReloadRoute(flag: boolean): void {
        this.storage.reloadRoute = flag;
        this.transitionStorage.next(this.storage);
    }

    reset(): void {
        this.storage = {
            chosenToState: '',
            assignToUser: {},
            transitions: [],
            valid: true,
            stateValid: {},
            transitionValid: true,
            callbackFunction: async () => {},
            preFunction: async () => {},
            title: 'Next Step:',
            errorMsg: 'Note: You do not meet the requirements to transition this item.',
            showErrorMsg: true,
            transitionToUserMessage: 'Assign to User in Region',
            messageToTransitionUser: '',
            transitioning: false,
            reloadRoute: false
        };
        this.transitionStorage.next(this.storage);
    }

    private checkIfResIsArray(res: any): boolean {
        if (Array.isArray(res)) {
            for (const item of res) {
                if (!item?.msg?.SUCCESS) return false;
            }
            return true;
        }
        return false;
    }
}
